import { useCallback, useEffect, useMemo, useRef } from 'react';

/** The debounced function, plus a way to make its pending call happen now. */
export type DebouncedCallback<TArgs extends unknown[]> = ((...args: TArgs) => void) & {
  /**
   * Runs any pending call immediately and cancels the timer. A no-op when
   * nothing is pending, so it is safe to call unconditionally.
   *
   * Exists for actions that read server-side what the debounce has not written
   * yet (issue #1): the digest routes refuse a matrix with no sujet/contexte,
   * and the front disables those buttons from the *draft* values. Between a
   * keystroke and the autosave landing, the two disagree — the user sees an
   * enabled button and the server sees an empty field. Flushing before the
   * call closes that window instead of asking the user to understand it.
   */
  flush: () => void;
};

/**
 * Returns a stable, debounced version of `callback`. Used across the wizard
 * for autosave (PATCH/PUT calls fired ~500ms after the last edit rather than
 * on every keystroke).
 *
 * The latest `callback` is always invoked (via a ref), so callers don't need
 * to worry about stale closures when the callback captures changing props/state.
 *
 * Any call still pending when the component unmounts is flushed immediately
 * rather than dropped — otherwise navigating to the next/previous wizard step
 * within the debounce window (e.g. clicking Suivant right after typing)
 * would silently lose that last edit, since each step component unmounts
 * when the wizard switches steps.
 */
export function useDebouncedCallback<TArgs extends unknown[]>(
  callback: (...args: TArgs) => void,
  delayMs: number,
): DebouncedCallback<TArgs> {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingArgsRef = useRef<TArgs | null>(null);

  const flush = useCallback(() => {
    if (!timeoutRef.current) return;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
    const pending = pendingArgsRef.current;
    pendingArgsRef.current = null;
    if (pending) callbackRef.current(...pending);
  }, []);

  const flushRef = useRef(flush);
  flushRef.current = flush;

  useEffect(() => {
    return () => {
      flushRef.current();
    };
  }, []);

  const schedule = useCallback(
    (...args: TArgs) => {
      pendingArgsRef.current = args;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        pendingArgsRef.current = null;
        callbackRef.current(...args);
      }, delayMs);
    },
    [delayMs],
  );

  return useMemo(() => Object.assign(schedule, { flush }), [schedule, flush]);
}
